/**
 * Contact-specific formatters for display formatting
 */

/**
 * Formats a full name from parts
 */
export const formatFullName = (
  firstName?: string,
  middleName?: string,
  lastName?: string,
  title?: string
): string => {
  const parts = [];
  if (title) parts.push(title);
  if (firstName) parts.push(firstName);
  if (middleName) parts.push(middleName);
  if (lastName) parts.push(lastName);
  return parts.join(' ').trim();
};

/**
 * Formats initials from name parts
 */
export const formatInitials = (
  firstName?: string,
  lastName?: string
): string => {
  const firstInitial = firstName?.charAt(0).toUpperCase() || '';
  const lastInitial = lastName?.charAt(0).toUpperCase() || '';
  return `${firstInitial}${lastInitial}`;
};

/**
 * Capitalizes first letter of each word
 */
export const formatTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Formats address for display
 */
export const formatAddress = (address: {
  businessName?: string;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  poBox?: string;
  country?: string;
}): string => {
  const parts = [];

  if (address.businessName) parts.push(address.businessName);
  if (address.poBox) parts.push(`PO Box ${address.poBox}`);
  if (address.streetAddress) parts.push(address.streetAddress);

  const cityStateZip = [];
  if (address.suburb) cityStateZip.push(address.suburb);
  if (address.state) cityStateZip.push(address.state);
  if (address.postcode) cityStateZip.push(address.postcode);

  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(' '));
  }

  if (address.country && address.country !== 'Australia') {
    parts.push(address.country);
  }

  return parts.join(', ');
};

/**
 * Formats address as multiple lines
 */
export const formatAddressLines = (address: {
  businessName?: string;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  poBox?: string;
  country?: string;
}): string[] => {
  const lines = [];

  if (address.businessName) lines.push(address.businessName);
  if (address.poBox) lines.push(`PO Box ${address.poBox}`);
  if (address.streetAddress) lines.push(address.streetAddress);

  const cityStateZip = [];
  if (address.suburb) cityStateZip.push(address.suburb);
  if (address.state) cityStateZip.push(address.state);
  if (address.postcode) cityStateZip.push(address.postcode);

  if (cityStateZip.length > 0) {
    lines.push(cityStateZip.join(' '));
  }

  if (address.country && address.country !== 'Australia') {
    lines.push(address.country);
  }

  return lines;
};

/**
 * Truncates text with ellipsis
 */
export const formatTruncate = (
  text: string,
  maxLength: number,
  suffix: string = '...'
): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
};

/**
 * Formats email for display (optionally obscured)
 */
export const formatEmail = (
  email: string,
  obscure: boolean = false
): string => {
  if (!obscure) return email.toLowerCase();

  const [username, domain] = email.split('@');
  if (!domain) return email;

  const obscuredUsername = username.substring(0, 2) + '***';
  return `${obscuredUsername}@${domain}`.toLowerCase();
};

/**
 * Formats role/position text
 */
export const formatRole = (role: string): string => {
  // Convert snake_case or kebab-case to Title Case
  return role
    .replace(/[-_]/g, ' ')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Collection of all contact formatters for easy import
 */
export const contactFormatters = {
  fullName: formatFullName,
  initials: formatInitials,
  titleCase: formatTitleCase,
  address: formatAddress,
  addressLines: formatAddressLines,
  truncate: formatTruncate,
  email: formatEmail,
  role: formatRole
};
